import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Wallet, auth } from "../lib/api.js";

const C = {
  navy: "#0E1A2E", navyDeep: "#070D1B", red: "#C8302A",
  amber: "#E8A317", green: "#0F8B3D", greenPale: "#E8F5E9",
  white: "#FFFFFF", cream: "#FAF7F1", sand: "#F0EADB",
  border: "#E0D9C8", line: "#EBE4D2",
  text: "#1A1A1A", text2: "#3A3A3A", text3: "#6A6A6A", text4: "#9A9A9A",
};

const PRESETS = [500, 1000, 2500, 5000, 10000, 25000];

export default function Recharge() {
  const navigate = useNavigate();
  const [amount, setAmount] = useState(1000);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [orderInfo, setOrderInfo] = useState(null);

  if (!auth.loggedIn()) {
    navigate("/login");
    return null;
  }

  const submit = async () => {
    setError(null);
    setBusy(true);
    try {
      const result = await Wallet.rechargeInitiate(amount * 100);
      // In production this returns Razorpay order_id + key — frontend opens Razorpay checkout.
      // Here we show what would happen so the engineer can wire it next.
      setOrderInfo(result);
    } catch (err) {
      setError(err.message || "Couldn't start recharge. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{
      minHeight: "100vh", background: C.cream,
      fontFamily: "-apple-system, system-ui, Inter, sans-serif",
    }}>
      {/* Top bar */}
      <div style={{
        background: C.white, borderBottom: `1px solid ${C.line}`, padding: "16px 24px",
      }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Link to="/dashboard" style={{ textDecoration: "none", display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{
              width: 32, height: 32, background: C.red, borderRadius: 8,
              display: "flex", alignItems: "center", justifyContent: "center",
              color: C.white, fontWeight: 800, fontSize: 16, letterSpacing: -1,
            }}>S</div>
            <div style={{ fontSize: 17, fontWeight: 800, color: C.navy, letterSpacing: -0.5 }}>Shiftswift</div>
          </Link>
          <Link to="/dashboard" style={{ fontSize: 13, color: C.text2, textDecoration: "none", fontWeight: 600 }}>
            ← Back to dashboard
          </Link>
        </div>
      </div>

      <div style={{ maxWidth: 560, margin: "0 auto", padding: "48px 24px" }}>
        <h1 style={{ fontSize: 32, fontWeight: 800, color: C.navy, margin: 0, letterSpacing: -1 }}>
          Recharge your wallet
        </h1>
        <p style={{ fontSize: 15, color: C.text3, marginTop: 8 }}>
          Add credit to your Shiftswift wallet. Use it to pay for shipments. Unused credit doesn't expire.
        </p>

        <div style={{
          background: C.white, borderRadius: 14, padding: 32, marginTop: 28,
          border: `1px solid ${C.line}`,
        }}>
          {orderInfo ? (
            <div>
              <div style={{
                background: C.greenPale, border: `1px solid ${C.green}`,
                color: C.navy, padding: 18, borderRadius: 10, marginBottom: 20,
              }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.green, marginBottom: 6 }}>
                  ✓ Recharge order created
                </div>
                <div style={{ fontSize: 13, lineHeight: 1.6 }}>
                  Order ID: <strong>{orderInfo.razorpay_order_id || orderInfo.order_id || "—"}</strong>
                </div>
                <div style={{ fontSize: 13, lineHeight: 1.6 }}>
                  Amount: <strong>₹{amount.toLocaleString("en-IN")}</strong>
                </div>
              </div>
              <p style={{ fontSize: 13, color: C.text3, lineHeight: 1.7, marginBottom: 20 }}>
                In production, this opens Razorpay Checkout to complete payment. Once Razorpay credentials
                are configured in <code style={{ background: C.cream, padding: "1px 6px", borderRadius: 3 }}>.env</code>,
                this page will load the Razorpay SDK and open the checkout modal automatically.
              </p>
              <button onClick={() => { setOrderInfo(null); setAmount(1000); }} style={{
                width: "100%", background: C.navy, color: C.white,
                border: "none", padding: 14, borderRadius: 8,
                fontSize: 14, fontWeight: 700, cursor: "pointer",
              }}>
                Recharge a different amount
              </button>
            </div>
          ) : (
            <>
              <div style={{ fontSize: 12, color: C.text3, fontWeight: 700, letterSpacing: 1, marginBottom: 12 }}>
                CHOOSE AMOUNT
              </div>
              <div style={{
                display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: 16,
              }}>
                {PRESETS.map((rs) => (
                  <button key={rs} onClick={() => setAmount(rs)} style={{
                    padding: "12px 8px",
                    background: amount === rs ? C.red : C.cream,
                    color: amount === rs ? C.white : C.navy,
                    border: `1px solid ${amount === rs ? C.red : C.line}`,
                    borderRadius: 8, fontSize: 14, fontWeight: 700,
                    cursor: "pointer", fontFamily: "inherit",
                  }}>
                    ₹{rs.toLocaleString("en-IN")}
                  </button>
                ))}
              </div>

              <div style={{ marginBottom: 18 }}>
                <label style={{ fontSize: 12, color: C.text3, fontWeight: 700, letterSpacing: 1, display: "block", marginBottom: 6 }}>
                  OR CUSTOM AMOUNT
                </label>
                <div style={{ display: "flex", alignItems: "stretch", border: `1px solid ${C.border}`, borderRadius: 8, overflow: "hidden" }}>
                  <span style={{ background: C.cream, padding: "12px 16px", fontSize: 16, fontWeight: 700, color: C.text2, borderRight: `1px solid ${C.border}` }}>
                    ₹
                  </span>
                  <input type="number" value={amount}
                    onChange={(e) => setAmount(parseInt(e.target.value) || 0)}
                    min={100} max={100000}
                    style={{
                      flex: 1, padding: "12px 14px", border: "none",
                      fontSize: 16, fontWeight: 600, fontFamily: "inherit", outline: "none",
                    }}
                  />
                </div>
                <div style={{ fontSize: 11, color: C.text4, marginTop: 4 }}>
                  Minimum ₹100. Maximum ₹1,00,000 per recharge.
                </div>
              </div>

              {error && (
                <div style={{
                  background: "#FEF2F2", border: "1px solid #FECACA", color: "#991B1B",
                  padding: "10px 12px", borderRadius: 8, fontSize: 13, marginBottom: 14,
                }}>
                  {error}
                </div>
              )}

              <button onClick={submit} disabled={busy || amount < 100} style={{
                width: "100%",
                background: (busy || amount < 100) ? C.text4 : C.red,
                color: C.white, border: "none", padding: 14, borderRadius: 8,
                fontSize: 15, fontWeight: 700,
                cursor: (busy || amount < 100) ? "not-allowed" : "pointer",
              }}>
                {busy ? "Starting recharge..." : `Recharge ₹${amount.toLocaleString("en-IN")}`}
              </button>

              <div style={{
                marginTop: 16, fontSize: 11, color: C.text4, textAlign: "center", lineHeight: 1.6,
              }}>
                Payment via Razorpay. Supports UPI, cards, net banking, and wallets.
                <br />
                Funds credit instantly after payment.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
